<template>
  <label class="p-checkbox__wrapper">
    <input
      :id="name"
      v-model="fieldValue"
      v-bind="{ ...$attrs, ...(inputAttributes as InputHTMLAttributes) }"
      :data-test="testAttribute"
      :class="['p-checkbox__field', `p-checkbox__field--${size}`]"
      type="checkbox"
      :error="errorState"
      @change="onChange"
    />
    <span v-if="$slots.append" class="p-checkbox__append">
      <slot name="append" />
    </span>
  </label>
</template>

<script setup lang="ts">
import type { InputHTMLAttributes, PropType } from 'vue'
import { useField } from 'vee-validate'

const emit = defineEmits(['update:modelValue', 'onChange'])

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: [Boolean, Array] as PropType<boolean | any[]>,
    required: true,
  },
  value: {
    type: [String, Number, Boolean],
    default: undefined,
  },
  multiSelect: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: (val: string) => ['sm', 'md'].includes(val),
  },
})
const testAttribute = dataTest(props.name)
const name = toRef(props, 'name')
const modelValue = toRef(props, 'modelValue')

// For checkbox groups (multiSelect), handle array logic
const isChecked = computed(() => {
  if (props.multiSelect && Array.isArray(modelValue.value)) {
    return modelValue.value.includes(props.value)
  }
  return Boolean(modelValue.value)
})

const {
  value: fieldValue,
  meta,
  setTouched,
} = useField(name, undefined, {
  initialValue: isChecked.value,
})

const inputAttributes = {
  name: props.name,
  disabled: props.disabled,
  value: props.value,
}

const errorState = computed<boolean>(() => !meta.valid && meta.touched)

const onChange = (event: Event) => {
  setTouched(true)
  const target = event.target as HTMLInputElement
  const checked = target.checked

  if (props.multiSelect && Array.isArray(modelValue.value)) {
    // Handle checkbox group (array)
    const currentArray = [...modelValue.value]
    if (checked) {
      if (!currentArray.includes(props.value)) {
        currentArray.push(props.value)
      }
    } else {
      const index = currentArray.indexOf(props.value)
      if (index > -1) {
        currentArray.splice(index, 1)
      }
    }
    emit('update:modelValue', currentArray)
    emit('onChange', currentArray)
  } else {
    // Handle single checkbox (boolean)
    emit('update:modelValue', checked)
    emit('onChange', checked)
  }
}

watch(
  () => isChecked.value,
  (newValue) => {
    fieldValue.value = newValue
  },
)

watch(
  () => modelValue.value,
  () => {
    if (props.multiSelect && Array.isArray(modelValue.value)) {
      fieldValue.value = modelValue.value.includes(props.value)
    } else {
      fieldValue.value = Boolean(modelValue.value)
    }
  },
)
</script>
